// components/AssignWorkerForm.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useUser } from "../../context/UserContext";
import { fetchWithAuth } from "../../utils/fetchWithAuth";
import GroupedSelect from "../UI/GroupedSelect";
import Modal from '../UI/Modal';
import Button from '../UI/Button';

const AssignWorkerForm = ({
    step,
    orderId,
    phaseIndex,
    stepIndex,
    departmentId,
    onUpdate,
    shiftSchedules,
}) => {
    const { logout } = useUser();
    const {
        handleSubmit: handleCreateSubmit,
        register: registerCreate,
        setValue: setValueCreate,
        reset: resetCreate,
        formState: { errors: createErrors },
    } = useForm();

    const {
        handleSubmit: handleEditSubmit,
        register: registerEdit,
        setValue: setValueEdit,
        reset: resetEdit,
        formState: { errors: editErrors },
    } = useForm();

    const [availableWorkers, setAvailableWorkers] = useState({});
    const [loading, setLoading] = useState(true);
    const [assignedWorkers, setAssignedWorkers] = useState([]);
    const [selectedWorker, setSelectedWorker] = useState(null);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingAssignee, setEditingAssignee] = useState(null);
    const [showAllAssignments, setShowAllAssignments] = useState(false);

    const fetchWorkers = useCallback(async () => {
        try {
            const workersRes = await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/orders/${departmentId}/users`,
                {},
                logout
            );

            const workersData = await workersRes.json();

            const formattedData = Object.entries(workersData).reduce(
                (acc, [position, users]) => {
                    acc[position] = users.map((user) => ({
                        value: user._id,
                        label: `${user.lastName} ${user.fullName} ${user.firstName} (${user.shiftSchedule})`,
                    }));
                    return acc;
                },
                {}
            );

            setAvailableWorkers(formattedData);

            if (step.assignees?.length > 0) {
                const workersInfo = await Promise.all(
                    step.assignees.map(async (assignee) => {
                        const [workerRes, assignedByRes] = await Promise.all([
                            fetchWithAuth(
                                `${process.env.REACT_APP_API_URL}/orders/manager/${assignee.userId}`,
                                {},
                                logout
                            ),
                            fetchWithAuth(
                                `${process.env.REACT_APP_API_URL}/orders/manager/${assignee.assignedBy}`,
                                {},
                                logout
                            ),
                        ]);
                        const [workerData, assignedByData] = await Promise.all([
                            workerRes.json(),
                            assignedByRes.json(),
                        ]);
                        return {
                            ...assignee,
                            workerInfo: workerData,
                            assignedByInfo: assignedByData,
                        };
                    })
                );
                setAssignedWorkers(workersInfo);
            }
        } catch (err) {
            console.error("Ошибка загрузки данных:", err);
        } finally {
            setLoading(false);
        }
    }, [departmentId, step.assignees, logout]);

    useEffect(() => {
        fetchWorkers();
    }, [fetchWorkers]);

    const handleCreateWorker = async (data) => {
        try {
			const alreadyAssigned = assignedWorkers.some(a =>
				a.userId === data.workerId &&
				new Date(a.date).toISOString().split('T')[0] === new Date(data.date).toISOString().split('T')[0] &&
				a.shiftSchedule === data.shiftSchedule
			  );
			  
			  if (alreadyAssigned) {
				alert("На эту дату и смену уже есть назначение для выбранного работника.");
				return;
			  }
			  
            await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/orders/${orderId}/assign-worker`,
                {
                    method: "POST",
                    body: JSON.stringify({
                        phaseIndex,
                        stepIndex,
                        userId: data.workerId,
                        quantity: data.quantity || 1,
                        date: data.date,
                        shiftSchedule: data.shiftSchedule
                    }),
                },
                logout
            );

            setSelectedWorker(null);
            resetCreate();
            setIsCreateModalOpen(false);
            await fetchWorkers();
            if (onUpdate) onUpdate();
        } catch (err) {
            console.error("Ошибка назначения рабочего:", err);
        }
    };

    const handleEditWorker = async (data) => {
        try {
            await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/orders/${orderId}/update-step`,
                {
                    method: "POST",
                    body: JSON.stringify({
                        phaseIndex,
                        stepIndex,
                        userId: editingAssignee.userId,
                        date: editingAssignee.date,
                        shiftSchedule: editingAssignee.shiftSchedule,
                        quantity: data.quantity,
                    }),
                },
                logout
            );

            setEditingAssignee(null);
            resetEdit();
            setIsEditModalOpen(false);
            await fetchWorkers();
            if (onUpdate) onUpdate();
        } catch (err) {
            console.error("Ошибка обновления назначения:", err);
        }
    };

    const handleEditClick = (assignee) => {
        setEditingAssignee(assignee);
        setValueEdit("quantity", assignee.quantity);
        setIsEditModalOpen(true);
    };

    const handleUnassign = async (assignee) => {
        try {
            await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/orders/${orderId}/unassign-worker`,
                {
                    method: "POST",
                    body: JSON.stringify({
                        phaseIndex,
                        stepIndex,
                        userId: assignee.userId,
                        date: assignee.date,
                        shiftSchedule: assignee.shiftSchedule,
                    }),
                },
                logout
            );
            await fetchWorkers();
            if (onUpdate) onUpdate();
        } catch (err) {
            console.error("Ошибка снятия назначения:", err);
        }
    };

    const handleCreateModalClose = () => {
        setIsCreateModalOpen(false);
        setSelectedWorker(null);
        resetCreate();
    };

    const handleEditModalClose = () => {
        setIsEditModalOpen(false);
        setEditingAssignee(null);
        resetEdit();
    };

    const handleWorkerSelect = (userId) => {
        setSelectedWorker(userId);
        setValueCreate("workerId", userId);
    };

    const visibleWorkers = showAllAssignments
        ? assignedWorkers
        : assignedWorkers.filter(assignee => assignee.status !== "done");
    
    const groupedAssignments = visibleWorkers.reduce((acc, assignee) => {
        const key = `${assignee.date}-${assignee.shiftSchedule}`;
        if (!acc[key]) {
            acc[key] = {
                date: assignee.date,
                shiftSchedule: shiftSchedules.find(shift => shift._id === assignee.shiftSchedule)?.name,
                assignments: []
            };
        }
        acc[key].assignments.push(assignee);
        return acc;
    }, {});

    if (loading) return <div>Загрузка...</div>;

    return (
        <div className="mt-2 space-y-2">
            <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex flex-col gap-2">
                    <div>
                        <p className="text-gray-400">
                            Норма времени: {step.duration} мин
                        </p>
                    </div>
                    <div>
                        <p className="text-gray-400">
                            Станок - {step.station || "Не указан"}
                        </p>
                    </div>
                </div>
                <div>
                    <div className="flex justify-between items-center mb-2">
                        <p className="text-gray-400 text-lg">Назначенные рабочие:</p>
                        <Button 
                            onClick={() => setIsCreateModalOpen(true)}
                            variant="primary"
                        >
                            Добавить назначение
                        </Button>
                    </div>
                    {Object.values(groupedAssignments).length > 0 ? (
                        <div className="space-y-4">
                            {Object.values(groupedAssignments).map((group) => (
                                <div key={`${group.date}-${group.shiftSchedule}`} className="border rounded p-2 bg-[#2D2D30] border-[#3E3E42]">
                                    <div className="mb-2">
                                        <p className="text-gray-400">
                                            Дата: {new Date(group.date).toLocaleDateString()}
                                        </p>
                                        <p className="text-gray-400">
                                            Смена: {group.shiftSchedule}
                                        </p>
                                    </div>
                                    <div className="space-y-2">
                                        {group.assignments.map((assignee) => (
                                            <div key={assignee.userId} className="border rounded p-2 bg-[#1E1E1E] border-[#3E3E42]">
                                                <div className="flex justify-between items-center gap-2">
                                                    <div className="flex flex-col gap-2">
                                                        <div className="flex items-center gap-2 text-gray-500">
                                                            <p className="font-medium">
                                                                {assignee.workerInfo?.fullName || "Неизвестный сотрудник"} {assignee.workerInfo?.lastName || "—"}
                                                            </p>
                                                            <p className="text-gray-500">
                                                                {assignee.workerInfo?.position?.name || "—"}
                                                            </p>
                                                        </div>
                                                        <div className="grid grid-cols-3 gap-2 text-sm text-gray-400">
                                                            <div>
                                                                <p>План: {assignee.quantity}</p>
                                                            </div>
                                                            <div>
                                                                <p>Годная продукция: {assignee.actualQuantity || 0}</p>
                                                            </div>
                                                            <div>
                                                                <p>Брак: {assignee.defectQuantity || 0}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {assignee.status !== "done" && (
                                                        <div className="flex gap-2">
                                                            <Button
                                                                onClick={() => handleEditClick(assignee)}
                                                                variant="edit"
                                                            >
                                                                Редактировать
                                                            </Button>
                                                            <Button
                                                                onClick={() => handleUnassign(assignee)}
                                                                variant="danger"
                                                            >
                                                                Снять назначение
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p>Нет назначенных рабочих</p>
                    )}
                    {assignedWorkers.some(assignee => assignee.status === "done") && (
                        <Button 
                            onClick={() => setShowAllAssignments(!showAllAssignments)}
                            className="mt-2"
                            variant="ghost"
                        >
                            {showAllAssignments ? "Скрыть завершенные" : "Показать все"}
                        </Button>
                    )}
                </div>
            </div>

            {/* Модальное окно создания */}
            <Modal 
                isOpen={isCreateModalOpen} 
                onClose={handleCreateModalClose} 
                title="Добавить назначение"
            >
                <form onSubmit={handleCreateSubmit(handleCreateWorker)} className="space-y-4">
                    <GroupedSelect
                        value={selectedWorker}
                        onChange={handleWorkerSelect}
                        options={availableWorkers}
                        placeholder="Выберите рабочего"
                    />
                    <input
                        type="date"
                        {...registerCreate("date", {
                            required: "Укажите дату"
                        })}
                        className="w-full p-2 rounded text-gray-400 bg-[#1E1E1E] border border-[#3E3E42] focus:outline-none focus:border-[#007ACC]"
                    />
                    {createErrors.date && (
                        <p className="text-sm text-red-500 mt-1">
                            {createErrors.date.message}
                        </p>
                    )}
                    <select
                        {...registerCreate("shiftSchedule", {
                            required: "Выберите смену"
                        })}
                        className="w-full p-2 rounded text-gray-400 bg-[#1E1E1E] border border-[#3E3E42] focus:outline-none focus:border-[#007ACC]"
                    >
                        <option value="">Выберите смену</option>
                        {shiftSchedules.map(shift => (
                            <option key={shift._id} value={shift._id}>
                                {shift.name}
                            </option>
                        ))}
                    </select>
                    {createErrors.shiftSchedule && (
                        <p className="text-sm text-red-500 mt-1">
                            {createErrors.shiftSchedule.message}
                        </p>
                    )}
                    <input
                        type="number"
                        {...registerCreate("quantity", {
                            required: "Укажите количество",
                            min: { value: 1, message: "Минимум 1" },
                        })}
                        placeholder="Количество"
                        className="w-full p-2 rounded text-gray-400 bg-[#1E1E1E] border border-[#3E3E42] focus:outline-none focus:border-[#007ACC]"
                    />
                    {createErrors.quantity && (
                        <p className="text-sm text-red-500 mt-1">
                            {createErrors.quantity.message}
                        </p>
                    )}

                    <div className="flex justify-end gap-2">
                        <Button 
                            type="button"
                            onClick={handleCreateModalClose}
                            className="bg-gray-600 hover:bg-gray-700"
                        >
                            Отмена
                        </Button>
                        <Button 
                            type="submit"
                            className="bg-blue-500 hover:bg-blue-600"
                        >
                            Назначить
                        </Button>
                    </div>
                </form>
            </Modal>

            {/* Модальное окно редактирования */}
            <Modal 
                isOpen={isEditModalOpen} 
                onClose={handleEditModalClose} 
                title="Редактировать назначение"
            >
                <form onSubmit={handleEditSubmit(handleEditWorker)} className="space-y-4">
                    <div className="text-gray-400">
                        <p className="font-medium">Рабочий: {editingAssignee?.workerInfo?.fullName} {editingAssignee?.workerInfo?.lastName}</p>
                        <p>Дата: {new Date(editingAssignee?.date).toLocaleDateString()}</p>
                        <p>Смена: {shiftSchedules.find(shift => shift._id === editingAssignee?.shiftSchedule)?.name}</p>
                    </div>
                    <input
                        type="number"
                        {...registerEdit("quantity", {
                            required: "Укажите количество",
                            min: { value: 1, message: "Минимум 1" },
                        })}
                        placeholder="Количество"
                        className="w-full p-2 rounded text-gray-400 bg-[#1E1E1E] border border-[#3E3E42] focus:outline-none focus:border-[#007ACC]"
                    />
                    {editErrors.quantity && (
                        <p className="text-sm text-red-500 mt-1">
                            {editErrors.quantity.message}
                        </p>
                    )}

                    <div className="flex justify-end gap-2">
                        <Button 
                            type="button"
                            onClick={handleEditModalClose}
                            className="bg-gray-600 hover:bg-gray-700"
                        >
                            Отмена
                        </Button>
                        <Button 
                            type="submit"
                            className="bg-blue-500 hover:bg-blue-600"
                        >
                            Сохранить
                        </Button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default AssignWorkerForm;
